import { useState, useEffect, useRef, memo, useMemo, useDeferredValue, useCallback } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import SeoHelmet from "@/components/SeoHelmet";
import { Link, useSearchParams, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, History, Trash2, ChevronLeft, ChevronRight, LogIn, LogOut, Loader2, FileText, Clock, Plus, Settings, Edit2, Menu, X, Linkedin, Chrome, Briefcase, Copy, Check, Share2, Twitter, MapPin, ExternalLink, DollarSign, Heart, LayoutGrid, LayoutList, ArrowRight } from "lucide-react";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { extractTextFromFile } from "@/lib/cvExtractor";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import CVUploader from "@/components/CVUploader";
import CVOutput from "@/components/CVOutput";
import { CVEditorSidebar } from "@/components/CVEditorSidebar";
import { useAuth } from "@/hooks/useAuth";
import { useSavedCVs } from "@/hooks/useSavedCVs";
import { useMasterResumes } from "@/hooks/useMasterResumes";
import { optimiseCVWithAI } from "@/lib/ai";
import { parseCV, stringifyCV, type CVData } from "@/lib/cvParser";
import CVTemplate from "@/components/CVTemplate";
import { OnboardingModal } from '@/components/OnboardingModal';
import { LinkedInOptimiser } from "@/components/LinkedInOptimiser";
import { CoverLetterGenerator } from "@/components/CoverLetterGenerator";
import { JobBoard } from "@/components/JobBoard";

import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useProfile } from "@/hooks/useProfile";
import { EXTENSION_ID } from "@/lib/extension-config";
import { supabase } from "@/lib/supabase";

const MemoizedCVOutput = memo(CVOutput);
const MemoizedCVTemplate = memo(CVTemplate);

declare const chrome: any;

// ─── SUB-COMPONENTS ────────────────────────────────────────────────────────

const BaseCVSidebar = ({ resumes, onUse, onDelete, user, session, setView, view, profile, saveResume, isOpen, onClose, isMobile, isCollapsed, onToggleCollapse }: any) => {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [isPasting, setIsPasting] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setUploading(true);
    try {
      const text = await extractTextFromFile(file);
      
      if (!text || text.trim().length === 0) {
        toast.error("Could not extract text from this PDF. Please try a .docx file instead or paste your CV text manually");
        return;
      }
      
      const name = file.name.replace(/\.[^/.]+$/, "") || "New Base CV";
      
      const existing = resumes.find((r: any) => r.name === name);
      if (existing) {
        toast.info("A CV with this name already exists");
        return;
      }
      
      await saveResume(name, text);
    } catch (err: any) {
      toast.error(err.message || "Failed to upload CV");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };


  return (
    <div className={`${isCollapsed ? "w-16" : "w-64"} border-r border-border bg-card flex flex-col h-full flex-shrink-0 transition-all duration-300 sidebar ${isOpen ? "open" : ""}`}>
      
      <div className={`p-3 border-b border-border flex items-center ${isCollapsed ? 'justify-center' : 'justify-end'}`}>
        {isMobile ? (
          <Button variant="ghost" size="icon" onClick={onClose} className="h-6 w-6 text-muted-foreground hover:text-red-400">
            <X className="w-4 h-4" />
          </Button>
        ) : (
          <Button variant="ghost" size="icon" onClick={onToggleCollapse} className="h-6 w-6 text-muted-foreground hover:text-foreground">
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        )}
      </div>

      <div className="p-3 border-b border-border space-y-1 overflow-hidden">
        <button onClick={() => setView("dashboard")} className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-2 px-3'} py-2 rounded-lg text-sm font-medium transition-colors ${view === 'dashboard' ? 'bg-cyan-500/10 text-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.1)]' : 'hover:bg-cyan-500/10 text-muted-foreground hover:text-cyan-500'}`}>
          <FileText className="w-5 h-5 text-cyan-500 flex-shrink-0" /> 
          {!isCollapsed && <span className="whitespace-nowrap">CV Dashboard</span>}
        </button>
        <button onClick={() => setView("linkedin")} className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-2 px-3'} py-2 rounded-lg text-sm font-medium transition-colors ${view === 'linkedin' ? 'bg-cyan-500/10 text-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.1)]' : 'hover:bg-cyan-500/10 text-muted-foreground hover:text-cyan-500'}`}>
          <Linkedin className="w-5 h-5 text-cyan-500 flex-shrink-0" />
          {!isCollapsed && <span className="whitespace-nowrap">LinkedIn Optimiser</span>}
        </button>
        <button onClick={() => setView("cover-letter")} className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-2 px-3'} py-2 rounded-lg text-sm font-medium transition-colors ${view === 'cover-letter' ? 'bg-cyan-500/10 text-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.1)]' : 'hover:bg-cyan-500/10 text-muted-foreground hover:text-cyan-500'}`}>
          <FileText className="w-5 h-5 text-cyan-500 flex-shrink-0" />
          {!isCollapsed && <span className="whitespace-nowrap">Cover Letter</span>}
        </button>
        <button onClick={() => setView("saved-jobs")} className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-2 px-3'} py-2 rounded-lg text-sm font-medium transition-colors ${view === 'saved-jobs' ? 'bg-cyan-500/10 text-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.1)]' : 'hover:bg-cyan-500/10 text-muted-foreground hover:text-cyan-500'}`}>
          <Heart className="w-5 h-5 text-cyan-500 flex-shrink-0" />
          {!isCollapsed && <span className="whitespace-nowrap">Saved Jobs</span>}
        </button>
        <button onClick={() => setView("jobs")} className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-2 px-3'} py-2 rounded-lg text-sm font-medium transition-colors ${view === 'jobs' ? 'bg-cyan-500/10 text-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.1)]' : 'hover:bg-cyan-500/10 text-muted-foreground hover:text-cyan-500'}`}>
          <Briefcase className="w-5 h-5 text-cyan-500 flex-shrink-0" />
          {!isCollapsed && <span className="whitespace-nowrap">Job Search</span>}
        </button>
        <button 
          onClick={() => window.open('https://chromewebstore.google.com/detail/alvaru-smart-job-apply/laoahcalgdmfecdbiodloilhcafeoafl', '_blank')} 
          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-2 px-3'} py-2 rounded-lg text-sm font-medium hover:bg-cyan-500/10 text-muted-foreground hover:text-cyan-500 transition-colors`}
        >
          <Chrome className="w-5 h-5 text-cyan-500 flex-shrink-0" />
          {!isCollapsed && <span className="whitespace-nowrap">Get Extension</span>}
        </button>
      </div>
      <div className={`p-4 border-b border-border flex items-center ${isCollapsed ? 'flex-col gap-2' : 'justify-between'}`}>
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Base CVs</h3>
            {uploading && <Loader2 className="w-3 h-3 animate-spin text-primary" />}
          </div>
        )}
        <div className={`flex items-center ${isCollapsed ? 'flex-col gap-2' : 'gap-1'}`}>
          <input 
            type="file" 
            id="sidebar-cv-upload"
            className="hidden" 
            accept=".pdf,.docx" 
            onChange={handleFileUpload} 
          />
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => document.getElementById('sidebar-cv-upload')?.click()}
            disabled={uploading}
            className="h-6 w-6 text-muted-foreground hover:bg-primary/10 hover:text-primary flex-shrink-0 transition-colors"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Manual Paste Dialog */}
      <Dialog open={isPasting} onOpenChange={setIsPasting}>
        <DialogContent className="sm:max-w-xl bg-card border-border shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-bold">Paste Your CV</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              If PDF upload is failing on your device, please paste your CV text here manually.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea 
              placeholder="Paste CV text here..." 
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              className="min-h-[300px] bg-muted/50 border-border focus:ring-primary text-sm resize-none"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" size="sm" onClick={() => setIsPasting(false)} className="text-[10px] font-bold uppercase tracking-widest">Cancel</Button>
            <Button 
              size="sm" 
              className="bg-primary hover:bg-primary/90 text-black text-[10px] font-bold uppercase tracking-widest px-6"
              disabled={!pasteText.trim()}
              onClick={async () => {
                const name = "Manual Paste " + new Date().toLocaleDateString();
                await saveResume(name, pasteText);
                setIsPasting(false);
                setPasteText("");
              }}
            >
              Save CV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="p-3 border-b border-border space-y-2 md:hidden">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => { setIsPasting(true); onClose(); }}
          className="w-full flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest border-dashed border-primary/30 hover:border-primary/60 hover:bg-primary/5 transition-all h-9"
        >
          <Plus className="w-3 h-3" /> Paste CV Manually
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
        {!user ? (
          <div className="py-8 px-2 text-center">
            <p className="text-[10px] text-muted-foreground uppercase mb-4">Sign in to sync</p>
            <Button size="sm" variant="outline" className="w-full text-[10px] h-8 border-border text-foreground hover:bg-muted" onClick={() => navigate("/auth")}>Sign In</Button>
          </div>
        ) : resumes.length === 0 ? (
          <div className="py-8 px-2 text-center opacity-30">
            <FileText className="w-8 h-8 mx-auto mb-2 text-foreground" />
            <p className="text-[10px] uppercase text-foreground">No base CVs</p>
          </div>
        ) : (
          resumes.map((r: any) => {
            const hasContent = !!(r.content?.trim() || r.raw_text?.trim() || r.text?.trim() || r.cv_text?.trim() || r.raw_result?.trim());
            return (
              <div 
                key={r.id} 
                onClick={() => {
                  console.log('CV fields:', JSON.stringify(Object.keys(r)));
                  console.log('CV content length:', (r.content || '').length, 'preview:', (r.content || '').substring(0, 100));
                  if (!hasContent) {
                    toast.error("CV content missing — please re-upload");
                    return;
                  }
                  onUse(r.content || r.raw_text || r.text || r.cv_text || r.raw_result || " ");
                }}
                className={`group relative border border-border/50 rounded-lg p-3 cursor-pointer transition-all flex flex-col gap-1 ${hasContent ? "bg-muted/40 hover:border-primary/40" : "bg-red-500/5 border-red-500/20"}`}
              >
                <div className={`flex items-start ${isCollapsed ? 'justify-center' : 'gap-3'} w-full`}>
                  <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${hasContent ? 'bg-primary/10' : 'bg-red-500/10'}`}>
                    <FileText className={`w-4 h-4 ${hasContent ? 'text-primary' : 'text-red-400'}`} />
                  </div>
                  {!isCollapsed && (
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate text-foreground/90 group-hover:text-primary transition-colors">{r.name}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">Updated {new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                  )}
                  {!isCollapsed && (
                    <button onClick={(e) => { e.stopPropagation(); onDelete(r.id); }} className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-red-400 transition-opacity">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {!hasContent && !isCollapsed && (
                  <div className="flex items-center justify-between pl-5 mt-1">
                    <p className="text-[9px] text-red-500 font-bold uppercase tracking-wider">CV content missing</p>
                    <button 
                      onClick={(e) => { e.stopPropagation(); onDelete(r.id); }}
                      className="px-2 py-1 bg-red-500/20 text-red-500 text-[9px] rounded font-bold uppercase hover:bg-red-500/40 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      <div className="mt-auto p-4 border-t border-border bg-card/50 overflow-hidden">
        {!isCollapsed && (
          <div className="h-1 bg-muted rounded-full overflow-hidden mb-2">
            <div 
              className="h-full bg-primary/60 transition-all duration-1000" 
              style={{ width: `${Math.min(100, ((profile?.credits_used || 0) / (profile?.credits_total || 5)) * 100)}%` }}
            />
          </div>
        )}
        {!isCollapsed && (
          <div className="flex justify-between items-center text-[10px] font-bold tracking-widest text-muted-foreground uppercase mb-3">
            <span className="truncate">{Math.min(profile?.credits_used || 0, profile?.credits_total || 5)} / {profile?.credits_total || 5} USED</span>
            <span className="text-primary truncate">{profile?.plan || 'Free'}</span>
          </div>
        )}
        <Button 
          onClick={() => navigate("/settings/subscription")}
          size="sm" 
          className={`w-full bg-primary hover:bg-primary/90 text-black h-8 shadow-lg shadow-primary/20 transition-all ${isCollapsed ? 'px-0' : 'text-[10px] font-bold uppercase tracking-wider'}`}
          title={isCollapsed ? "Upgrade" : ""}
        >
          {isCollapsed ? <Sparkles className="w-4 h-4" /> : "Upgrade"}
        </Button>
      </div>
    </div>
  );
};

const ProjectHistory = ({ projects, onLoad, onDelete, onUpdateTitle, onUpdateStatus, onShare, loading, viewMode }: any) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  if (loading) return (
    <div className="flex-1 flex items-center justify-center bg-background transition-colors">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  );

  const handleStartEdit = (e: React.MouseEvent, p: any) => {
    e.stopPropagation();
    setEditingId(p.id);
    setEditValue(p.title || "");
  };

  const handleSave = async (id: string) => {
    if (editValue.trim()) {
      await onUpdateTitle(id, editValue.trim());
    }
    setEditingId(null);
  };

  const getStatusStyle = (status: string) => {
    const isPreview = status === 'PREVIEW';
    const isApplied = status === 'APPLIED';
    return {
      dot: isPreview ? 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]' : 
           isApplied ? 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.4)]' : 
           'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]',
      text: isPreview ? 'text-amber-500/90' : 
            isApplied ? 'text-blue-500/90' : 
            'text-green-500/90',
      label: isPreview ? 'Pending' : isApplied ? 'Applied' : 'Ready',
      trackingLabel: isPreview ? 'Reviewing' : isApplied ? 'Applied' : status
    };
  };

  if (viewMode === 'grid') {
    return (
      <div className="flex-1 p-8 overflow-y-auto custom-scrollbar bg-background transition-colors">
        {projects.length === 0 ? (
          <div className="bg-card border border-border rounded-2xl p-20 text-center shadow-xl">
            <div className="opacity-20 mb-3 text-foreground"><History className="w-12 h-12 mx-auto" /></div>
            <p className="text-muted-foreground text-sm">No projects yet. Click "New CV" to start.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p: any) => {
              const s = getStatusStyle(p.status);
              return (
                <motion.div
                  key={p.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="group bg-card border border-border hover:border-cyan-500/50 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col gap-3"
                >
                  {/* Card header - editable title */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      {editingId === p.id ? (
                        <input
                          autoFocus
                          className="bg-muted border border-primary/30 rounded px-2 py-1 text-sm text-foreground w-full focus:outline-none focus:border-primary"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => handleSave(p.id)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSave(p.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <p
                          className="text-sm font-semibold text-foreground hover:text-primary transition-colors cursor-pointer truncate"
                          onClick={(e) => handleStartEdit(e, p)}
                          title={p.title}
                        >
                          {p.title}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleStartEdit(e, p); }}
                      className="p-1 text-muted-foreground hover:text-primary transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                      title="Edit Filename"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Company & Date */}
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span className="font-medium text-foreground/80 truncate">{p.company || "Direct Application"}</span>
                    <span className="text-muted-foreground/30">•</span>
                    <span className="text-muted-foreground/60 whitespace-nowrap">{new Date(p.created_at).toLocaleDateString("en-GB")}</span>
                  </div>

                  {/* Status & Tracking row */}
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <div className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${s.text}`}>{s.label}</span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-muted/80 border border-border/80 hover:border-primary/30 transition-colors cursor-pointer">
                          <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">{s.trackingLabel}</span>
                          <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-32 bg-card border-border shadow-2xl">
                        <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => onUpdateStatus(p.id, 'PREVIEW')}>Reviewing</DropdownMenuItem>
                        <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => onUpdateStatus(p.id, 'APPLIED')}>Applied</DropdownMenuItem>
                        <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => onUpdateStatus(p.id, 'INTERVIEW')}>Interview</DropdownMenuItem>
                        <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => onUpdateStatus(p.id, 'REJECTED')}>Rejected</DropdownMenuItem>
                        <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => onUpdateStatus(p.id, 'OFFER')}>Offer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 pt-1 border-t border-border/50 mt-auto">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        onLoad(p.id, p.title, p.company);
                      }}
                      className="flex-1 h-8 text-[10px] font-bold uppercase tracking-wider text-primary hover:text-primary/80 hover:bg-primary/5"
                    >
                      View CV
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); onShare?.(p.id); }}
                      className="flex-1 h-8 text-[10px] font-bold uppercase tracking-wider text-cyan-500 hover:text-cyan-400 hover:bg-cyan-500/10"
                    >
                      Share
                    </Button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onDelete(p.id); }}
                      className={`p-2 transition-all hover:scale-110 ${p.status === 'PREVIEW' ? 'text-amber-500/60 hover:text-red-400' : 'opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-400'}`}
                      title={p.status === 'PREVIEW' ? "Cancel Job" : "Delete"}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Table view (default) ──
  return (
    <div className="flex-1 p-8 overflow-y-auto custom-scrollbar bg-background transition-colors">
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-xl dark:shadow-2xl dashboard-table-container">
        <table className="w-full text-left border-collapse cv-history-table">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Name</th>
              <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Company</th>
              <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Date</th>
              <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Status</th>
              <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Tracking</th>
              <th className="px-6 py-4 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {projects.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-20 text-center">
                  <div className="opacity-20 mb-3 text-foreground"><History className="w-12 h-12 mx-auto" /></div>
                  <p className="text-muted-foreground text-sm">No projects yet. Click "New CV" to start.</p>
                </td>
              </tr>
            ) : (
              projects.map((p: any) => {
                const s = getStatusStyle(p.status);
                return (
                  <tr key={p.id} className="group hover:bg-muted/40 transition-all border-t border-border/10">
                    <td className="px-6 py-4" data-label="Name">
                      {editingId === p.id ? (
                        <input
                          autoFocus
                          className="bg-muted border border-primary/30 rounded px-2 py-1 text-sm text-foreground w-full focus:outline-none focus:border-primary"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => handleSave(p.id)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSave(p.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <p
                            className="text-sm font-semibold text-foreground hover:text-primary transition-colors cursor-pointer"
                            onClick={(e) => handleStartEdit(e, p)}
                          >
                            {p.title}
                          </p>
                          <button
                            onClick={(e) => handleStartEdit(e, p)}
                            className="p-1 text-muted-foreground hover:text-primary transition-colors opacity-0 group-hover:opacity-100"
                            title="Edit Filename"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4" data-label="Company">
                      <p className="text-[11px] text-muted-foreground font-medium">{p.company || "Direct Application"}</p>
                    </td>
                    <td className="px-6 py-4" data-label="Date">
                      <p className="text-[11px] text-muted-foreground/60">{new Date(p.created_at).toLocaleDateString("en-GB")}</p>
                    </td>
                    <td className="px-6 py-4" data-label="Status">
                      <div className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${s.text}`}>{s.label}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-label="Tracking">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-muted/80 border border-border/80 group-hover:border-primary/30 transition-colors cursor-pointer">
                            <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">{s.trackingLabel}</span>
                            <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
                          </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-32 bg-card border-border shadow-2xl">
                          <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => onUpdateStatus(p.id, 'PREVIEW')}>Reviewing</DropdownMenuItem>
                          <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => onUpdateStatus(p.id, 'APPLIED')}>Applied</DropdownMenuItem>
                          <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => onUpdateStatus(p.id, 'INTERVIEW')}>Interview</DropdownMenuItem>
                          <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => onUpdateStatus(p.id, 'REJECTED')}>Rejected</DropdownMenuItem>
                          <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => onUpdateStatus(p.id, 'OFFER')}>Offer</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                    <td className="px-6 py-4 text-right" data-label="Action">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            onLoad(p.id, p.title, p.company);
                          }}
                          className="h-8 text-[10px] font-bold uppercase tracking-wider text-primary hover:text-primary/80 hover:bg-primary/5"
                        >
                          View CV
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); onShare?.(p.id); }}
                          className="h-8 text-[10px] font-bold uppercase tracking-wider text-cyan-500 hover:text-cyan-400 hover:bg-cyan-500/10"
                        >
                          Share
                        </Button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onDelete(p.id); }}
                          className={`p-2 transition-all hover:scale-110 ${p.status === 'PREVIEW' ? 'text-amber-500/60 hover:text-red-400' : 'opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-400'}`}
                          title={p.status === 'PREVIEW' ? "Cancel Job" : "Delete"}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SavedJobsList = ({ jobs, onTailor, onRemove, loading }: any) => {
  if (loading) return (
    <div className="flex-1 flex items-center justify-center bg-background transition-colors min-h-[300px]">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="flex-1 p-8 overflow-y-auto custom-scrollbar bg-background transition-colors">
      {jobs.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-20 text-center shadow-xl">
          <div className="opacity-20 mb-3 text-foreground"><Briefcase className="w-12 h-12 mx-auto" /></div>
          <p className="text-muted-foreground text-sm font-semibold mb-2">No saved jobs yet.</p>
          <p className="text-muted-foreground/60 text-xs">Bookmark jobs from Job Search to see them here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {jobs.map((job: any) => (
            <motion.div
              key={job.id}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="group bg-white dark:bg-slate-900/50 border border-border hover:border-cyan-500/50 rounded-2xl p-5 transition-all shadow-sm hover:shadow-md flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative overflow-hidden"
            >
              <div className="flex items-center gap-4 flex-1 min-w-0 z-10">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {(job.company || 'J')[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-bold text-foreground mb-1 truncate pr-4 group-hover:text-cyan-500 transition-colors">
                    {job.job_title}
                  </h3>
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground/80">{job.company}</span>
                    {job.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 opacity-70" />
                        {job.location}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      Posted {new Date(job.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 w-full md:w-auto z-10">
                <Button
                  size="sm"
                  className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white dark:text-black font-semibold px-4 h-8 text-xs"
                  onClick={(e) => { e.stopPropagation(); onTailor(job); }}
                >
                  Tailor CV
                  <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); onRemove(job.id); }}
                  className="h-8 w-8 rounded-full text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── MAIN APP PAGE ─────────────────────────────────────────────────────────
const AppPage = () => {
  const { view: pathView, id: pathId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user, signOut: originalSignOut, session } = useAuth();
  const view = (pathView as "dashboard" | "inputs" | "editor" | "linkedin" | "cover-letter" | "jobs") || "dashboard";
  const currentId = (pathId === 'undefined' || !pathId) ? null : pathId;
  const [dashboardTab, setDashboardTab] = useState<'cvs' | 'jobs'>('cvs');
  const [savedJobs, setSavedJobs] = useState<any[]>([]);
  const [loadingSavedJobs, setLoadingSavedJobs] = useState(false);

  const fetchSavedJobs = useCallback(async () => {
    if (!user?.id) return;
    setLoadingSavedJobs(true);
    try {
      const { data, error } = await supabase
        .from("saved_jobs")
        .select(`
          job_id,
          job_recommendations (*)
        `)
        .eq("user_id", user.id);
      
      if (error) throw error;

      const mappedJobs: any[] = data
        .filter(f => f.job_recommendations)
        .map(f => ({
          ...f.job_recommendations,
          id: f.job_id
        }));

      setSavedJobs(mappedJobs.sort((a: any, b: any) => (b.relevance_score || 0) - (a.relevance_score || 0)));
    } catch (err: any) {
      console.error("Error fetching saved jobs on dashboard:", err);
    } finally {
      setLoadingSavedJobs(false);
    }
  }, [user?.id]);

  const handleUnsaveJob = async (jobId: string) => {
    if (!user?.id) return;
    try {
      const { error } = await supabase
        .from("saved_jobs")
        .delete()
        .eq("user_id", user.id)
        .eq("job_id", jobId);

      if (error) throw error;
      toast.success("Job removed from saved jobs");
      setSavedJobs(prev => prev.filter(j => j.id !== jobId));
    } catch (err: any) {
      console.error("Error removing saved job:", err);
      toast.error("Failed to remove saved job");
    }
  };

  const handleTailorSavedJob = (job: any) => {
    setTargetJobTitle(job.job_title);
    setTargetCompany(job.company);
    setJobDescription(job.description || "");
    
    if (resumes.length > 1) {
      setCvText(""); 
      setView("inputs");
    } else if (resumes.length === 1) {
      setCvText(resumes[0].content);
      setView("inputs");
    } else {
      setView("inputs");
    }
    toast.success("Job details pre-filled. Ready to tailor your CV!");
  };

  useEffect(() => {
    if (view === 'saved-jobs' && user?.id) {
      fetchSavedJobs();
    }
  }, [view, user?.id, fetchSavedJobs]);

  const [cvText, setCvText] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [targetJobTitle, setTargetJobTitle] = useState("");
  const [targetCompany, setTargetCompany] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);
  const [showEditorPanel, setShowEditorPanel] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showNewCvDialog, setShowNewCvDialog] = useState(false);
  const [newCvName, setNewCvName] = useState("");
  const [cvViewMode, setCvViewMode] = useState<'table' | 'grid'>('table');
  
  // Wrapped signOut for additional logging and extension notification
  const signOut = async () => {
    console.log('[Alvaru Debug] AppPage: signOut triggered');
    try {
      if (typeof chrome !== 'undefined' && chrome.runtime?.sendMessage) {
        chrome.runtime.sendMessage(EXTENSION_ID, { action: 'ALVARU_LOGOUT' });
      }
      await originalSignOut();
      navigate('/auth');
    } catch (err) {
      console.error('[Alvaru Debug] Sign out failed:', err);
      toast.error("Sign out failed — please clear cookies manually");
    }
  };

  console.log('AppPage: Render');
  const { profile, refetchProfile } = useProfile();
  const { 
    savedCVs, 
    loadingCVs, 
    saveCV, 
    deleteCV, 
    updateCVTitle, 
    updateCVContent,
    fetchFullCV,
    refetch
  } = useSavedCVs({ onCreditChange: refetchProfile });
  const { resumes, saveResume, deleteResume } = useMasterResumes();
  const [optimiseStatus, setOptimiseStatus] = useState("");
  const [editableData, setEditableData] = useState<CVData | null>(null);
  const editableDataRef = useRef<CVData | null>(null);
  const lastLocalEditTimeRef = useRef<number>(0);
  
  useEffect(() => {
    editableDataRef.current = editableData;
    if (editableData) {
      lastLocalEditTimeRef.current = Date.now();
    }
  }, [editableData]);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const memoizedSetEditableData = useCallback((raw: string) => {
    try {
      if (typeof raw === 'string') {
        setEditableData(parseCV(raw));
      } else {
        console.warn("[Alvaru] Non-string passed to memoizedSetEditableData", raw);
      }
    } catch (e) {
      console.error("[Alvaru] Failed to parse CV data in memo:", e);
    }
  }, []);

  const parsedResult = useMemo(() => {
    if (!result || typeof result !== 'string') return null;
    try {
      return parseCV(result);
    } catch (e) {
      console.error("[Alvaru] Failed to parse CV data in useMemo:", e);
      return null;
    }
  }, [result]);

  const deferredData = useDeferredValue(editableData || parsedResult);

  // URL param state (LinkedIn integration)
  const [searchParams] = useSearchParams();
  const [currentSavedCvId, setCurrentSavedCvId] = useState<string>("");
  // linkedInSource is derived live from searchParams so it works on BOTH the inputs
  // URL (?source=linkedin) and the editor URL (/app/editor/{id}?source=linkedin)
  const linkedInSource = searchParams.get('source') === 'linkedin';
  // useRef instead of useState: avoids stale-closure issues and prevents re-render loops
  const hasAutoOptimiseParam = useRef(searchParams.get('autoOptimise') === 'true');
  const hasAutoOptimiseFired = useRef(false);
  const [applyingToLinkedIn, setApplyingToLinkedIn] = useState(false);
  const [userToken, setUserToken] = useState("");

  const generatingMap = useRef<Record<string, boolean>>({});
  const lastSavedContentRef = useRef<string>("");
  // Stable ref for fetchFullCV — prevents useEffect re-runs when session refreshes
  const fetchFullCVRef = useRef(fetchFullCV);
  useEffect(() => { fetchFullCVRef.current = fetchFullCV; }, [fetchFullCV]);
  // Guard: tracks which CV ID has already been fetched to prevent duplicate DB calls
  const fetchedIdRef = useRef<string>("");

  const handleUpdateStatus = async (id: string, status: "PREVIEW" | "READY" | "APPLIED") => {
    const template = localStorage.getItem("alvaru_selected_template") || "Classic";
    const success = await updateCVStatus(id, status, template);
    if (success) {
      toast.success(`Status updated to ${status}`);
    } else {
      toast.error("Failed to update status");
    }
  };

  const getBaseUrl = () => {
    if (window.location.hostname === 'localhost') {
      return 'http://localhost:8080';
    }
    return 'https://alvaru.com';
  };

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");

  const handleShare = async (id: string) => {
    try {
      const { error } = await supabase
        .from('saved_cvs')
        .update({ is_public: true, public_slug: id })
        .eq('id', id);
      
      if (error) throw error;
      
      const link = `${getBaseUrl()}/cv/view/${id}`;
      setShareLink(link);
      setShareModalOpen(true);
    } catch (err) {
      console.error("Failed to share CV:", err);
      toast.error("Failed to generate share link");
    }
  };




  // Debounced auto-save for editableData
  useEffect(() => {
    if (view === "editor" && currentId && editableData && user) {
      const handler = setTimeout(async () => {
        const rawString = stringifyCV(editableData);
        if (rawString === lastSavedContentRef.current) return;
        
        console.log('[Alvaru Sync] Auto-save triggered — ID:', currentId);
        lastSavedContentRef.current = rawString;
        
        // Try updating saved_cvs first via the hook (which handles raw_result and triggers any re-renders)
        const success = await updateCVContent(currentId, rawString);
        
        if (!success) {
          // If failed (likely because it's a master_resumes ID), update master_resumes!
          console.log('[Alvaru Sync] Not in saved_cvs, updating master_resumes instead for ID:', currentId);
          const contentToSave = JSON.stringify(editableData);
          const { error: masterError } = await supabase
            .from("master_resumes")
            .update({ 
              content: contentToSave,
              name: editableData.name || "Untitled Scratch CV"
            })
            .eq("id", currentId)
            .eq("user_id", user.id);

          if (masterError) {
            console.warn("Failed to sync resume to Supabase master_resumes:", masterError.message);
          } else {
            console.log('[Alvaru Sync] Successfully updated master_resumes!');
          }
        } else {
          console.log('[Alvaru Sync] Successfully updated saved_cvs!');
        }
      }, 500); // Faster save (500ms)
      return () => clearTimeout(handler);
    }
  }, [editableData, currentId, view, user]);

  // Force save on window unload
  useEffect(() => {
    const handleUnload = () => {
      if (view === "editor" && currentId && editableData && user) {
        const rawString = stringifyCV(editableData);
        if (rawString !== lastSavedContentRef.current) {
          supabase
            .from("saved_cvs")
            .update({ raw_result: rawString })
            .eq("id", currentId)
            .eq("user_id", user.id)
            .then(({ data, error }) => {
              // If we couldn't update saved_cvs, try master_resumes
              if (error || !data || data.length === 0) {
                supabase
                  .from("master_resumes")
                  .update({ 
                    content: JSON.stringify(editableData),
                    name: editableData.name || "Untitled Scratch CV"
                  })
                  .eq("id", currentId)
                  .eq("user_id", user.id)
                  .then(() => {});
              }
            });
        }
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [editableData, currentId, view, user]);

  // Populate session token for LinkedIn button
  useEffect(() => {
    if (user) {
      supabase.auth.getSession().then(({ data }) => {
        setUserToken(data.session?.access_token || "");
      });
    }
  }, [user]);

  // Populate fields from URL params once on mount (LinkedIn redirect flow)
  useEffect(() => {
    const jt = searchParams.get('jobTitle');
    const co = searchParams.get('company');
    const jd = searchParams.get('jobDescription');
    if (jt) setTargetJobTitle(jt);
    if (co) setTargetCompany(co);
    if (jd) setJobDescription(jd);
    // Navigate to inputs, preserving ?source=linkedin so it survives the route change
    if ((jt || jd) && view === 'dashboard') {
      const source = searchParams.get('source');
      const qs = source ? `?source=${encodeURIComponent(source)}` : '';
      navigate(`/app/inputs${qs}`);
    }
  }, []); // run once only

  // Auto-optimise: fires when resumes & user are ready AND URL had autoOptimise=true
  // Checks jobDescription STATE (not searchParams) because searchParams may be gone
  // after navigation to /app/inputs. useRef flag prevents repeat triggers.
  useEffect(() => {
    if (!hasAutoOptimiseParam.current || hasAutoOptimiseFired.current) return;
    if (resumes.length === 0 || !user) return;
    const cv = resumes[0].content || '';
    if (!cv) return;
    // jobDescription state is set by the populate effect above — use it, not searchParams
    if (!jobDescription.trim()) return;
    hasAutoOptimiseFired.current = true;
    setCvText(cv);
    toast.info('Auto-optimising for LinkedIn job...');
    setTimeout(() => runOptimise(cv), 200);
  }, [resumes, user, jobDescription]);

  useEffect(() => {
    if (user && !localStorage.getItem('alvaru_onboarding_complete')) {
      setShowOnboarding(true);
    }
  }, [user]);


  // Helper to change view and update URL using navigation.
  // Pass preserveSource=true to forward ?source=linkedin into the new URL.
  const setView = (newView: "dashboard" | "inputs" | "editor" | "linkedin" | "cover-letter", id?: string, preserveSource = false) => {
    const source = searchParams.get('source');
    const qs = (preserveSource && source) ? `?source=${encodeURIComponent(source)}` : '';
    if (id) {
      navigate(`/app/${newView}/${id}${qs}`);
    } else {
      navigate(`/app/${newView}${qs}`);
    }
  };

  // Restore CV from savedCVs list (Fastest, in-memory)
  useEffect(() => {
    if (view === "editor" && currentId && !result && savedCVs.length > 0) {
      const project = savedCVs.find(p => p.id === currentId);
      // Note: savedCVs list fetch doesn't usually include raw_result, but saveCV adds it to memory
      if (project?.raw_result) {
        console.log("[Alvaru Load] Restoring from in-memory list");
        setResult(project.raw_result);
        setEditableData(parseCV(project.raw_result));
      }
    }
  }, [view, currentId, savedCVs, result]);

  // Direct Fetch from DB on Load (Reliable for Refresh/Deep Link)
  useEffect(() => {
    // Only fetch once per currentId — fetchedIdRef prevents re-runs when fetchFullCV reference changes
    if (view === "editor" && currentId && !result && user && fetchedIdRef.current !== currentId) {
      fetchedIdRef.current = currentId;
      console.log("[Alvaru Load] Direct fetch triggered for ID:", currentId);
      
      const fetchFromMasterResumes = async () => {
        console.log("[Alvaru Load] Checking master_resumes for ID:", currentId);
        const { data: masterData } = await supabase
          .from("master_resumes")
          .select("content, name")
          .eq("id", currentId)
          .eq("user_id", user.id)
          .maybeSingle();

        if (masterData) {
          console.log("[Alvaru Load] Found in master_resumes");
          let parsed;
          try {
            parsed = JSON.parse(masterData.content);
            setEditableData(parsed);
            setResult(""); // Don't set JSON string to result to prevent useEffect overwrite
          } catch (e) {
            parsed = parseCV(masterData.content);
            setEditableData(parsed);
            setResult(masterData.content); // If it's plain text, we can set it
          }
        } else {
          console.warn("[Alvaru Load] Not found in master_resumes either");
          toast.error("Could not load CV. It may have been deleted or is corrupted.");
          setView("dashboard");
        }
      };

      fetchFullCVRef.current(currentId).then(cv => {
        if (cv?.raw_result && typeof cv.raw_result === 'string') {
          setResult(cv.raw_result);
          try {
            setEditableData(parseCV(cv.raw_result));
          } catch (e) {
            console.error("[Alvaru Load] Failed to parse fetched CV:", e);
          }
          console.log("[Alvaru Load] Direct fetch successful");
        } else {
          fetchFromMasterResumes();
        }
      }).catch(err => {
        console.error("[Alvaru Load] Direct fetch error from saved_cvs:", err);
        fetchFromMasterResumes();
      });
    }
  }, [view, currentId, result, user]);

  // Helper to refresh data from Supabase (manual trigger)
  const handleRefresh = async () => {
    if (!currentId) return;
    setIsRefreshing(true);
    try {
      const { data, error } = await supabase
        .from("saved_cvs")
        .select("raw_result")
        .eq("id", currentId)
        .single();
      
      if (data?.raw_result) {
        setResult(data.raw_result);
        setEditableData(parseCV(data.raw_result));
        toast.info("CV refreshed from extension");
      } else if (error) {
        throw error;
      }
    } catch (err: any) {
      console.error("Refresh failed:", err);
      toast.error("Manual refresh failed");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Real-time synchronization subscription (Extension -> Website)
  useEffect(() => {
    if (!currentId || view !== "editor") return;

    console.log("Alvaru Realtime: Subscribing to external updates for CV", currentId);
    
    const channel = supabase
      .channel(`editor-sync-${currentId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "saved_cvs",
          filter: `id=eq.${currentId}`,
        },
        async (payload) => {
          if (payload.new && payload.new.raw_result) {
            // Ignore database updates if the user has edited the CV locally in the last 10 seconds
            if (Date.now() - lastLocalEditTimeRef.current < 10000) {
              console.log("Alvaru Realtime: Ignoring DB update due to recent local edit");
              return;
            }

            const currentContent = editableDataRef.current ? stringifyCV(editableDataRef.current) : '';
            const payloadRawResult = payload.new.raw_result;

            if (payloadRawResult === currentContent || payloadRawResult === lastSavedContentRef.current) {
              return;
            }
            
            setResult(payload.new.raw_result);
            setEditableData(parseCV(payload.new.raw_result));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentId, view]);

  // Fallback Polling Mechanism (Extension -> Website)
  useEffect(() => {
    if (!currentId || view !== "editor") return;

    const interval = setInterval(() => {
      // Ignore polling updates if the user has edited the CV locally in the last 10 seconds
      if (Date.now() - lastLocalEditTimeRef.current < 10000) {
        return;
      }

      fetchFullCVRef.current(currentId).then(cv => {
        if (cv && cv.raw_result) {
          const currentContent = editableDataRef.current ? stringifyCV(editableDataRef.current) : "";
          if (cv.raw_result !== currentContent && cv.raw_result !== lastSavedContentRef.current) {
            console.log("Alvaru Polling: Change detected, updating editor");
            setResult(cv.raw_result);
            setEditableData(parseCV(cv.raw_result));
          }
        }
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [currentId, view]);


  // Reset to dashboard when result is cleared — but NOT if we have a currentId in the URL,
  // because on a hard refresh the CV will be reloaded from savedCVs asynchronously.
  useEffect(() => {
    if (!result && view === "editor" && !currentId) {
      setView("dashboard");
    }
  }, [result]);

  // Sync the user's latest master CV to the extension on load
  useEffect(() => {
    if (!user || resumes.length === 0) return;
    const latestCV = resumes[0].content;
    if (!latestCV) return;

    if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
      try {
        chrome.runtime.sendMessage(
          EXTENSION_ID,
          { action: 'ALVARU_SAVE_CV', cv: latestCV }
        );
      } catch (err) {
        // Silently fail if extension not present
      }
    }
  }, [user, resumes]);

  // Handle loading an existing CV from history
  const handleLoadCV = async (id: string, title?: string, company?: string) => {
    console.log('VIEW CV clicked - id:', id, 'title:', title, 'company:', company);
    setLoading(true);
    setOptimiseStatus("Loading tailored CV...");
    
    try {
      const fullCV = await fetchFullCV(id);
      console.log('fullCV.raw_result length:', fullCV?.raw_result?.length);
      console.log('fullCV.raw_result first 200 chars:', fullCV?.raw_result?.substring(0, 200));
      console.log('fullCV.raw_result includes newline:', fullCV?.raw_result?.includes('\n'));
      
      if (fullCV) {
        setResult(fullCV.raw_result);
        if (fullCV.job_description) setJobDescription(fullCV.job_description);
        if (company || fullCV.company) setTargetCompany(company || fullCV.company || "");
        
        // Clear targeted job title so generateCVFilename uses the parsed title from the CV content
        setTargetJobTitle(""); 
        setCurrentSavedCvId(id);
        console.log('About to call setView with editor, id:', id);
        setView("editor", id);
        console.log('setView called');
      } else {
        console.warn('handleLoadCV: fullCV is null or undefined after fetch');
      }
    } catch (err) {
      console.error('handleLoadCV: Error fetching CV:', err);
    } finally {
      setLoading(false);
      setOptimiseStatus("");
    }
  };

  const handleCreateFromScratch = () => {
    if (!user) {
      toast.error("Please sign in to create a CV");
      return;
    }
    setNewCvName("");
    setShowNewCvDialog(true);
  };

  const submitCreateFromScratch = async () => {
    if (!newCvName.trim()) {
      toast.error("Please enter a name for your CV");
      return;
    }
    
    setShowNewCvDialog(false);
    setLoading(true);
    setOptimiseStatus("Creating CV...");
    
    const emptyCvData = {
      name: "",
      jobTitle: "",
      basicInfo: ["", "", "", ""],
      profile: "",
      keySkills: [],
      workExperience: [],
      education: [],
      interests: [],
      references: "",
      sectionNames: {
        basicInfo: "Basic Information",
        profile: "Profile",
        keySkills: "Key Skills",
        workExperience: "Work Experience",
        education: "Education",
        interests: "Interests",
        references: "References"
      }
    };
    
    const contentString = JSON.stringify(emptyCvData);
    
    try {
      const { data, error } = await supabase
        .from("saved_cvs")
        .insert({
          user_id: user.id,
          title: newCvName.trim(),
          company: "Custom Built",
          raw_result: contentString,
          status: "completed"
        })
        .select()
        .single();
        
      if (error) {
        console.error("Error creating empty CV in saved_cvs:", error);
        toast.error("Could not create empty CV: " + error.message);
      } else if (data) {
        toast.success("Created empty CV successfully!");
        setResult(""); // Prevent useEffect from overwriting editableData with JSON
        setEditableData(emptyCvData);
        setCurrentSavedCvId(data.id);
        
        setView("editor", data.id);
      }
    } catch (err: any) {
      console.error("Unexpected error:", err);
      toast.error("Could not create empty CV");
    } finally {
      setLoading(false);
      setOptimiseStatus("");
    }
  };

  // When result is set from handleOptimise, switch to editor
  useEffect(() => {
    if (result && view !== "editor") {
      memoizedSetEditableData(result);
      setView("editor");
    } else if (result) {
      memoizedSetEditableData(result);
    }
  }, [result, memoizedSetEditableData]);

  const handleTextExtracted = async (text: string) => {
    
    if (!text || text.trim().length === 0) {
      toast.error("Could not extract text from this PDF. Please try a .docx file instead or paste your CV text manually");
      return;
    }
    
    setCvText(text);

    // Send to extension
    if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
      try {
        chrome.runtime.sendMessage(EXTENSION_ID, {
          action: 'ALVARU_SAVE_CV',
          cv: text
        });
      } catch (err) {
        // Silently fail if extension not present
      }
    }

    if (user) {
      let nameLine = text.split("\n")[0].trim();
      if (nameLine.length > 100) nameLine = nameLine.substring(0, 97) + "...";
      nameLine = nameLine.toUpperCase() || "Master Resume";
      
      const existing = resumes.find((r: any) => r.name === nameLine);
      if (existing) {
        toast.info("A CV with this name already exists");
        return;
      }
      
      if (!resumes.some(r => r.content.slice(0, 100) === text.slice(0, 100))) {
        await saveResume(nameLine, text);
      }
    }
  };

  // Core optimise — accepts optional cv override (used by auto-optimise from URL params)
  const runOptimise = async (cvOverride?: string) => {
    const theCV = cvOverride || cvText;
    if (!theCV.trim()) { toast.error("Please upload or paste your CV first"); return; }
    const jd = searchParams.get('jobDescription') || jobDescription;
    const jt = searchParams.get('jobTitle') || targetJobTitle;
    const co = searchParams.get('company') || targetCompany;
    if (!jd.trim()) { toast.error("Please paste the job description"); return; }

    if (profile && profile.credits_used >= profile.credits_total) {
      toast.error("You've used all your credits. Please upgrade your plan to continue.", {
        action: {
          label: "Upgrade",
          onClick: () => navigate("/settings/subscription")
        },
        duration: 5000
      });
      return;
    }

    setLoading(true);
    setResult("");
    setOptimiseStatus("Analyzing CV & Job Description...");
    try {
      setTimeout(() => setOptimiseStatus("Extracting relevant keywords..."), 1000);
      setTimeout(() => setOptimiseStatus("Applying ATS optimization rules..."), 3000);
      setTimeout(() => setOptimiseStatus("Finalizing layout and structure..."), 5000);
      const aiData = await optimiseCVWithAI(theCV, jd, jt, co);
      const aiResult = aiData.result;
      setResult(aiResult);
      toast.success("CV optimised successfully!");
      refetchProfile();
      if (user) {
        const template = localStorage.getItem("alvaru_selected_template") || "Classic";
        const saved = await saveCV(
          aiResult,
          jd,
          jt,
          co,
          aiData.candidateFirstName,
          aiData.candidateLastName,
          "READY",
          template
        );
        if (saved?.id) {
          setCurrentSavedCvId(saved.id);
          // Preserve ?source=linkedin so the editor page shows the Apply on LinkedIn button
          setView("editor", saved.id, true);
        }
      }
    } catch (error: any) {
      console.error("Optimise error:", error);
      const isCreditError = error.message?.toLowerCase().includes("credits") || error.message?.includes("403");
      
      if (isCreditError) {
        toast.error("You've used all your credits. Please upgrade your plan to continue.", {
          action: {
            label: "Upgrade",
            onClick: () => navigate("/settings/subscription")
          },
          duration: 5000
        });
      } else {
        toast.error(error.message || "Failed to optimise CV");
      }
    } finally {
      setLoading(false);
      setOptimiseStatus("");
    }
  };

  const handleOptimise = async () => {
    if (!cvText.trim()) { toast.error("Please upload or paste your CV first"); return; }
    if (!jobDescription.trim()) { toast.error("Please paste the job description"); return; }
    await runOptimise();
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background text-foreground transition-colors max-w-full">
      <SeoHelmet
        title="Your AI CV Dashboard"
        description="Access your Alvaru dashboard. Tailor new CVs, manage your job applications, and use AI to optimize your resume for any role."
        canonical="https://www.alvaru.com/app"
        noindex={true}
      />
      <nav className="border-b border-border z-10 relative bg-card/80 backdrop-blur-md">
        <div className="w-full flex items-center justify-between h-14 px-6">
          <div className="flex items-center gap-8">
            <div className="flex items-center">
              <button 
                onClick={() => { setSidebarOpen(true); setDesktopSidebarOpen(true); }}
                className={`hamburger ${desktopSidebarOpen ? 'md:hidden' : ''}`}
              >
                <Menu className="w-5 h-5" />
              </button>
              <Link to="/" onClick={() => { setView("dashboard"); setResult(""); }} className="flex items-center">
                <img src="/logo.png" alt="Alvaru" className="h-14 w-auto" />
              </Link>
            </div>
            
            {(view === "dashboard" || view === "inputs" || view === "jobs") && (
              <div className="hidden md:flex items-center bg-muted/50 border border-border rounded-lg px-3 py-1.5 w-80 group focus-within:border-primary/50 transition-colors">
                <input 
                  type="text" 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search your optimisations..." 
                  className="bg-transparent border-none text-xs text-foreground/60 placeholder:text-muted-foreground focus:outline-none w-full"
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0 overflow-hidden border border-primary/30 bg-primary/20 hover:bg-primary/30 transition-all active:scale-95">
                  <span className="text-[10px] font-bold text-primary">{user?.email?.charAt(0).toUpperCase() || "A"}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-card border-border shadow-2xl rounded-xl p-1" align="end" sideOffset={8}>
                <div className="flex items-center gap-3 p-3 border-b border-border/50 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
                    {user?.email?.charAt(0).toUpperCase() || "A"}
                  </div>
                  <div className="flex flex-col space-y-0.5 max-w-[140px]">
                    <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-[0.2em]">Profile</p>
                    <p className="text-[11px] font-semibold truncate text-foreground/90">{user?.email}</p>
                  </div>
                </div>
                <DropdownMenuItem 
                  onClick={() => window.open('https://chromewebstore.google.com/detail/alvaru-smart-job-apply/laoahcalgdmfecdbiodloilhcafeoafl', '_blank')} 
                  className="gap-3 py-2.5 px-3 rounded-lg cursor-pointer focus:bg-cyan-500 focus:text-black transition-colors"
                >
                  <Chrome className="w-4 h-4" />
                  <span className="font-bold text-[10px] uppercase tracking-wider">Extension</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => navigate("/settings/subscription")} 
                  className="gap-3 py-2.5 px-3 rounded-lg cursor-pointer focus:bg-primary focus:text-black transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  <span className="font-bold text-[10px] uppercase tracking-wider">Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-border/50 mx-2 my-1" />
                <DropdownMenuItem 
                  onSelect={(e) => {
                    e.preventDefault();
                    signOut();
                  }}
                  className="gap-3 py-2.5 px-3 rounded-lg cursor-pointer text-red-400 focus:bg-destructive focus:text-white transition-all"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="font-bold text-[10px] uppercase tracking-wider">Sign Out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      <div className="flex-1 flex overflow-hidden">
        {/* BASE CVS SIDEBAR (Persistent in Dashboard and Inputs view) */}
        {(view === "dashboard" || view === "inputs" || view === "jobs" || view === "linkedin" || view === "cover-letter" || view === "saved-jobs") && (
          <>
            <div 
              className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`} 
              onClick={() => setSidebarOpen(false)} 
            />
            {(isMobile ? sidebarOpen : true) && (
              <BaseCVSidebar 
                resumes={resumes} 
                user={user} 
                session={session}
                onUse={(raw: string) => { setCvText(raw); setView("inputs"); setSidebarOpen(false); toast.success("Base CV loaded"); }}
                onDelete={deleteResume}
                setView={setView}
                view={view}
                profile={profile}
                saveResume={saveResume}
                isOpen={sidebarOpen}
                onClose={() => { setSidebarOpen(false); setDesktopSidebarOpen(false); }}
                isMobile={isMobile}
                isCollapsed={!isMobile && !desktopSidebarOpen}
                onToggleCollapse={() => setDesktopSidebarOpen(!desktopSidebarOpen)}
              />
            )}
          </>
        )}

        {/* MAIN CONTENT AREA */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {view === "dashboard" && (
            <div className="flex-1 flex flex-col min-w-0 bg-background transition-colors overflow-y-auto custom-scrollbar">
              <header className="h-16 border-b border-border bg-card/30 flex items-center justify-between px-8 flex-shrink-0 transition-colors">
                <div className="flex items-center gap-4">
                  <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-foreground">Dashboard</h2>
                  {/* View toggle */}
                  <div className="flex items-center bg-muted/60 border border-border rounded-lg p-0.5">
                    <button
                      onClick={() => setCvViewMode('table')}
                      className={`p-1.5 rounded-md transition-all ${cvViewMode === 'table' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                      title="Table view"
                    >
                      <LayoutList className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCvViewMode('grid')}
                      className={`p-1.5 rounded-md transition-all ${cvViewMode === 'grid' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                      title="Grid view"
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleCreateFromScratch}
                    variant="outline"
                    className="border-primary/30 hover:border-primary text-foreground text-xs font-bold py-1.5 h-9 px-4 flex items-center gap-2 rounded-lg shadow-lg"
                  >
                    <Plus className="w-4 h-4 text-primary" /> Create New
                  </Button>
                  <Button 
                    onClick={() => {
                      if (resumes.length > 1) {
                        setCvText(""); // Ensure we show selection
                        setView("inputs");
                      } else if (resumes.length === 1) {
                        setCvText(resumes[0].content);
                        setView("inputs");
                      } else {
                        setView("inputs");
                      }
                    }}
                    className="bg-primary hover:bg-primary/90 text-black text-xs font-bold py-1.5 h-9 px-4 flex items-center gap-2 rounded-lg shadow-lg shadow-primary/20"
                  >
                    <Sparkles className="w-4 h-4 animate-pulse" /> Tailor CV with AI
                  </Button>
                </div>
              </header>
              
              {user && resumes.length === 0 && (
                <div className="mx-8 mt-6">
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex items-center justify-between group hover:bg-primary/10 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-[11px] font-bold text-foreground uppercase tracking-widest">No Base CV Uploaded</p>
                        <p className="text-[10px] text-muted-foreground">Upload your master resume to start tailoring for jobs.</p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      onClick={() => setShowOnboarding(true)}
                      className="text-[10px] font-bold text-primary hover:text-primary hover:bg-primary/10 uppercase tracking-widest"
                    >
                      Upload Now →
                    </Button>
                  </div>
                </div>
              )}

              <ProjectHistory 
                projects={savedCVs.filter(cv => 
                  cv.title?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                  cv.company?.toLowerCase().includes(searchQuery.toLowerCase())
                )} 
                onLoad={handleLoadCV}
                onDelete={deleteCV}
                onUpdateTitle={updateCVTitle}
                onUpdateStatus={handleUpdateStatus}
                onShare={handleShare}
                loading={loadingCVs}
                viewMode={cvViewMode}
              />
            </div>
          )}

          {view === 'saved-jobs' && (
            <div className="flex-1 flex flex-col min-w-0 bg-background transition-colors overflow-y-auto custom-scrollbar">
              <header className="h-16 border-b border-border bg-card/30 flex items-center justify-between px-8 flex-shrink-0 transition-colors">
                <div className="flex items-center gap-4">
                  <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-foreground">Saved Jobs</h2>
                </div>
                
                <div className="hidden md:flex items-center bg-muted/50 border border-border rounded-lg px-3 py-1.5 w-80 group focus-within:border-primary/50 transition-colors">
                  <input 
                    type="text" 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search saved jobs..." 
                    className="bg-transparent border-none text-xs text-foreground/60 placeholder:text-muted-foreground focus:outline-none w-full"
                  />
                </div>
              </header>

              <SavedJobsList 
                jobs={savedJobs.filter(job => 
                  job.job_title?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                  job.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  job.description?.toLowerCase().includes(searchQuery.toLowerCase())
                )}
                onTailor={handleTailorSavedJob}
                onRemove={handleUnsaveJob}
                loading={loadingSavedJobs}
              />
            </div>
          )}

          {view === 'jobs' && (
            <JobBoard 
              userId={user?.id}
              userCV={(() => {
                const activeResume = resumes.find((r: any) => !!(r.content?.trim() || r.raw_text?.trim() || r.text?.trim() || r.cv_text?.trim() || r.raw_result?.trim()));
                return activeResume ? (activeResume.content || activeResume.raw_text || activeResume.text || activeResume.cv_text || activeResume.raw_result || "") : "";
              })()}
              onTailor={(job) => {
                setTargetJobTitle(job.job_title);
                setTargetCompany(job.company);
                setJobDescription(job.description);
                setView("inputs", undefined, true);
                toast.success("Job details pre-filled. Ready to tailor your CV!");
              }} 
            />
          )}

          {view === 'linkedin' && (
            <LinkedInOptimiser onBack={() => setView("dashboard")} />
          )}

          {view === "cover-letter" && (
            <CoverLetterGenerator 
              onBack={() => setView("dashboard")} 
              initialLetterId={currentId}
              onLetterSelected={(id) => setView("cover-letter", id)}
            />
          )}

          {view === "inputs" && (
            <div className="flex-1 overflow-y-auto p-12 custom-scrollbar bg-background/50">
              <div className="max-w-4xl mx-auto space-y-8">
                <div className="flex items-center gap-4 mb-4">
                  <Button variant="ghost" size="sm" onClick={() => setView("dashboard")} className="text-muted-foreground hover:text-foreground">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                  <h2 className="text-xl font-bold text-foreground">Create New Optimization</h2>
                </div>

                <div className="grid lg:grid-cols-2 gap-8">
                  {/* Left — Input */}
                  <div className="space-y-6 text-left">
                    <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest block">Step 1: Your Base CV</label>
                    {cvText ? (
                      <div className="bg-card rounded-xl p-6 border border-border shadow-lg relative group transition-all hover:border-primary/30">
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="sm" onClick={() => setCvText("")} className="text-[10px] h-7 text-muted-foreground hover:text-red-400">
                            <Trash2 className="w-3 h-3 mr-1" /> Change
                          </Button>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-bold text-sm text-foreground truncate max-w-[300px]">
                              Using: {(() => {
                                const m: any = resumes.find((r: any) => (r.content || r.raw_text || r.text || r.cv_text || r.raw_result || " ") === cvText);
                                return (m?.name || m?.title || m?.filename || "Original Upload").split('\n')[0].substring(0, 80);
                              })()}
                            </h3>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-[0.1em] font-bold">Ready for tailoring</p>
                          </div>
                        </div>
                      </div>
                    ) : resumes.length > 0 ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 gap-2">
                          {resumes.map((r: any) => {
                            const hasContent = !!(r.content?.trim() || r.raw_text?.trim() || r.text?.trim() || r.cv_text?.trim() || r.raw_result?.trim());
                            return (
                              <button
                                key={r.id}
                                onClick={() => {
                                  console.log('CV fields:', JSON.stringify(Object.keys(r)));
                                  console.log('CV content length:', (r.content || '').length, 'preview:', (r.content || '').substring(0, 100));
                                  if (!hasContent) {
                                    toast.error("CV content missing — please re-upload");
                                    return;
                                  }
                                  setCvText(r.content || r.raw_text || r.text || r.cv_text || r.raw_result || " ");
                                }}
                                className={`flex items-center justify-between p-4 bg-card border border-border rounded-xl transition-all group ${hasContent ? 'hover:border-primary/50 hover:bg-primary/5' : 'opacity-70 grayscale'}`}
                              >
                                <div className="flex items-center gap-3 text-left">
                                  <div className={`p-2 rounded-lg transition-colors ${hasContent ? 'bg-muted group-hover:bg-primary/10' : 'bg-red-500/10'}`}>
                                    <FileText className={`w-4 h-4 ${hasContent ? 'text-muted-foreground group-hover:text-primary' : 'text-red-400/70'}`} />
                                  </div>
                                  <div>
                                    <span className="text-sm font-semibold block">{r.name}</span>
                                    {!hasContent && <span className="text-[10px] text-red-500 font-bold tracking-wider uppercase mt-1 block">CV content missing — please re-upload</span>}
                                  </div>
                                </div>
                                {hasContent ? (
                                  <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-all" />
                                ) : (
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); deleteResume(r.id); }}
                                    className="px-3 py-1.5 bg-red-500/20 text-red-500 text-[10px] rounded-lg font-bold uppercase hover:bg-red-500/40 transition-colors z-10 relative"
                                  >
                                    Remove
                                  </button>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex items-center gap-4 py-2">
                          <div className="h-px bg-border flex-1" />
                          <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest">or</span>
                          <div className="h-px bg-border flex-1" />
                        </div>
                        <CVUploader onTextExtracted={handleTextExtracted} onPasteStart={() => setSidebarOpen(false)} />
                      </div>
                    ) : (
                      <CVUploader onTextExtracted={handleTextExtracted} onPasteStart={() => setSidebarOpen(false)} />
                    )}

                    <div className="space-y-4">
                      <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest block">Step 2: Target Information</label>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5 text-left">
                          <Label className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Job Title</Label>
                          <input type="text" placeholder="e.g. Senior Designer" value={targetJobTitle} onChange={(e) => setTargetJobTitle(e.target.value)} className="w-full bg-card border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 text-foreground placeholder:text-muted-foreground/50 transition-colors" />
                        </div>
                        <div className="space-y-1.5 text-left">
                          <Label className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Company</Label>
                          <input type="text" placeholder="e.g. Google" value={targetCompany} onChange={(e) => setTargetCompany(e.target.value)} className="w-full bg-card border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 text-foreground placeholder:text-muted-foreground/50 transition-colors" />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 text-left">
                      <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest block">Step 3: Job Description</label>
                      <textarea placeholder="Paste requirements here..." className="w-full h-40 bg-card border border-border rounded-xl p-4 text-sm resize-none focus:outline-none focus:border-primary/50 text-foreground placeholder:text-muted-foreground/50 transition-colors" value={jobDescription} onChange={(e) => setJobDescription(e.target.value)} />
                    </div>

                    <Button variant="hero" className="w-full font-heading font-semibold text-base py-6 button-glow bg-primary text-black hover:bg-primary/90" onClick={handleOptimise} disabled={loading}>
                      {loading ? <div className="flex items-center gap-3"><Loader2 className="w-5 h-5 animate-spin" /><span className="animate-pulse">{optimiseStatus}</span></div> : <><Sparkles className="w-5 h-5" /><span>Optimise My CV</span></>}
                    </Button>
                  </div>

                  {/* Right — Helper */}
                  <div className="hidden lg:flex flex-col items-center justify-center p-8 bg-card/30 border border-dashed border-border rounded-2xl">
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-6 opacity-40">
                      <Sparkles className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-foreground font-bold mb-2">AI-Powered Optimization</h3>
                    <p className="text-muted-foreground text-xs text-center max-w-sm leading-relaxed px-4">
                      Our AI will analyze your base CV and the job description to highlight relevant skills and rewrite achievements for maximum impact.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {view === "editor" && (
            (result || editableData) ? (
              <div className="flex w-full h-full overflow-hidden relative min-w-0">
                {/* Desktop Sidebar / Mobile Overlay */}
                {!isMobile ? (
                  <motion.div
                    initial={{ x: -400, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    className="flex-shrink-0 w-auto border-r border-border bg-card overflow-y-auto h-full"
                  >
                    <CVEditorSidebar 
                      data={editableData || parsedResult} 
                      onChange={setEditableData} 
                    />
                  </motion.div>
                ) : (
                  showEditorPanel && (
                    <div className="fixed inset-0 z-[100] bg-background overflow-y-auto flex flex-col">
                      <div className="p-4 border-b border-border flex items-center justify-between bg-card sticky top-0 z-10">
                        <div className="flex items-center gap-2">
                          <Edit2 className="w-4 h-4 text-primary" />
                          <h3 className="text-sm font-bold uppercase tracking-widest">Edit CV Sections</h3>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => setShowEditorPanel(false)} className="rounded-full">
                          <X className="w-5 h-5" />
                        </Button>
                      </div>
                      <div className="flex-1 pb-20">
                        <CVEditorSidebar 
                          data={editableData || parsedResult} 
                          onChange={setEditableData} 
                        />
                      </div>
                      <div className="p-4 bg-card border-t border-border sticky bottom-0">
                        <Button onClick={() => setShowEditorPanel(false)} className="w-full bg-primary text-black font-bold">
                          Done Editing
                        </Button>
                      </div>
                    </div>
                  )
                )}
                
                {/* Mobile FAB */}
                {isMobile && !showEditorPanel && (
                  <button 
                    className="fixed bottom-6 right-6 z-40 bg-primary text-black px-6 py-4 rounded-full shadow-[0_10px_40px_rgba(0,0,0,0.3)] flex items-center gap-3 font-bold active:scale-95 transition-transform animate-in fade-in slide-in-from-bottom-4 duration-500"
                    onClick={() => setShowEditorPanel(true)}
                  >
                    <Edit2 className="w-5 h-5" />
                    <span>Edit CV</span>
                  </button>
                )}
                
                <div className="flex-1 h-full relative overflow-x-auto editor-preview-container cv-preview-container">
                  {/* Apply on LinkedIn button — shown when opened from extension */}
                  {linkedInSource && currentSavedCvId && (
                    <div className="absolute top-3 right-4 z-20">
                      <Button
                        onClick={async () => {
                          if (!user || !userToken || !currentSavedCvId) {
                            toast.error('Please sign in and optimise a CV first');
                            return;
                          }
                          setApplyingToLinkedIn(true);
                          try {
                            await new Promise<void>((resolve, reject) => {
                              if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
                                reject(new Error('Extension not detected'));
                                return;
                              }
                              chrome.runtime.sendMessage(
                                EXTENSION_ID,
                                {
                                  action: 'APPLY_ON_LINKEDIN',
                                  cvId: currentSavedCvId,
                                  userId: user.id,
                                  token: userToken
                                },
                                (response: any) => {
                                  if (chrome.runtime.lastError) {
                                    reject(new Error(chrome.runtime.lastError.message));
                                  } else if (response?.ok) {
                                    resolve();
                                  } else {
                                    reject(new Error(response?.error || 'Extension error'));
                                  }
                                }
                              );
                            });
                            toast.success('Sending to LinkedIn Easy Apply... Switch to your LinkedIn tab.');
                          } catch (err: any) {
                            toast.error(err.message || 'Failed to connect to extension. Make sure it is installed and LinkedIn is open.');
                          } finally {
                            setApplyingToLinkedIn(false);
                          }
                        }}
                        disabled={applyingToLinkedIn}
                        className="bg-[#0A66C2] hover:bg-[#004182] text-white font-bold text-xs h-9 px-4 flex items-center gap-2 rounded-lg shadow-lg"
                      >
                        {applyingToLinkedIn ? (
                          <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending to LinkedIn...</>
                        ) : (
                          <>
                            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                            Apply on LinkedIn
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  {!deferredData && (
                    <div className="flex-1 h-full flex flex-col items-center justify-center gap-4 text-muted-foreground animate-in fade-in duration-500">
                      <Loader2 className="w-8 h-8 animate-spin text-primary/50" />
                      <p className="text-xs font-bold uppercase tracking-widest opacity-50">Restoring CV Session...</p>
                    </div>
                  )}
                  {deferredData && (
                    <div className={editableData !== deferredData ? "opacity-50 transition-opacity duration-300" : "opacity-100 transition-opacity duration-300"}>
                      <MemoizedCVOutput 
                        data={deferredData} 
                        onUpdate={setEditableData}
                        loading={loading} 
                        jobTitle={targetJobTitle} 
                        company={targetCompany}
                        isFullScreen={true}
                        onBack={() => setView("dashboard")}
                        onRefresh={handleRefresh}
                        isRefreshing={isRefreshing}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 h-full flex flex-col items-center justify-center gap-6 text-muted-foreground animate-in fade-in duration-500">
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full" />
                  <Loader2 className="w-12 h-12 animate-spin text-primary relative z-10" />
                </div>
                <div className="text-center space-y-2 relative z-10">
                  <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-foreground">Initialising Editor</h3>
                  <p className="text-[10px] font-medium uppercase tracking-widest opacity-50">Fetching your tailored content from the cloud...</p>
                </div>
              </div>
            )
          )}

          {/* Share Dialog */}
          <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
            <DialogContent className="sm:max-w-md bg-card border-border shadow-2xl">
              <DialogHeader>
                <DialogTitle>Share this CV</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-4 py-4">
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={shareLink}
                    className="flex-1 rounded-md border border-input bg-muted px-3 py-2 text-sm text-foreground"
                  />
                  <Button size="icon" variant="outline" onClick={() => {
                    navigator.clipboard.writeText(shareLink);
                    toast.success("Link copied to clipboard!");
                  }}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center justify-center gap-4 mt-2">
                  <Button
                    variant="outline"
                    className="flex-1 gap-2 border-border hover:bg-muted text-foreground"
                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink)}`, '_blank')}
                  >
                    <Linkedin className="h-4 w-4 text-[#0A66C2]" />
                    LinkedIn
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 gap-2 border-border hover:bg-muted text-foreground"
                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareLink)}&text=Check out my tailored CV!`, '_blank')}
                  >
                    <Twitter className="h-4 w-4 text-[#1DA1F2]" />
                    X
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* New CV Dialog */}
          <Dialog open={showNewCvDialog} onOpenChange={setShowNewCvDialog}>
            <DialogContent className="sm:max-w-md bg-card border-border shadow-2xl">
              <DialogHeader>
                <DialogTitle>Name Your New CV</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-4 py-4">
                <input
                  autoFocus
                  placeholder="e.g. Graphic Designer CV"
                  value={newCvName}
                  onChange={(e) => setNewCvName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitCreateFromScratch();
                  }}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <Button 
                  onClick={submitCreateFromScratch} 
                  className="w-full font-bold bg-primary text-black hover:bg-primary/90"
                  disabled={!newCvName.trim()}
                >
                  Create CV
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <OnboardingModal
            open={showOnboarding}
            onClose={() => setShowOnboarding(false)}
            onUpload={saveResume}
            onPasteStart={() => setSidebarOpen(false)}
            onComplete={() => {
              localStorage.setItem('alvaru_onboarding_complete', 'true');
              setShowOnboarding(false);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AppPage;
